import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import Sidebar from '../../components/sidebar/sidebar';
import Header from '../../components/header';
import Card from '../../components/Card';
import TenantSettings from './TenantSettings';
import Theme from './Theme';
import DataRefreshTab from './DataRefreshTab';
import CacheManagementTab from './CacheManagementTab';

function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const role = auth?.userData?.profile?.role ?? 'tenantadmin';
  const allowedRoles = ['tenantadmin', 'globaladmin', 'reseller'];
  const canViewTenantSettings = allowedRoles.includes(role as string);
  const canViewProcessData = role === 'globaladmin';

  // Determine active tab from URL
  const getActiveTab = () => {
    if (location.pathname.includes('/settings/theme')) return 'Theme';
    if (canViewTenantSettings && location.pathname === '/settings') return 'TenantSettings';
    return 'Theme';
  };

  const [activeTab, setActiveTab] = useState<'TenantSettings' | 'Theme' | 'DataRefresh' | 'CacheManagement'>(getActiveTab());

  useEffect(() => {
    // If user doesn't have access to TenantSettings and that's the current tab, redirect to Theme
    if (!canViewTenantSettings && activeTab === 'TenantSettings') {
      setActiveTab('Theme');
      navigate('/settings/theme');
    }
  }, [canViewTenantSettings, activeTab, navigate]);

  const handleTabChange = (tab: 'TenantSettings' | 'Theme' | 'DataRefresh' | 'CacheManagement') => {
    setActiveTab(tab);
    if (tab === 'TenantSettings') {
      navigate('/settings');
    }
    // else if (tab === 'Theme') {
    //     navigate('/settings/theme')
    // }
    // No navigation for DataRefresh/CacheManagement for now
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'TenantSettings':
        return <TenantSettings />;
      case 'DataRefresh':
        return <DataRefreshTab />;
      case 'CacheManagement':
        return <CacheManagementTab />;
      default:
        return <Theme />;
    }
  };

  const tabs = [
    ...(canViewTenantSettings ? ['Tenant Settings'] : []),
    ...(canViewProcessData ? ['Process Data'] : []),
    ...(canViewProcessData ? ['Cache Data'] : []),
  ];

  return (
    <div className="flex">
      <Sidebar />
      <div className="bg-[var(--Background-Color,#161617)] flex-1">
        <Header title="Settings" dropdown={2} />
        <Card className="flex-1">
          <div>
            {tabs.length > 1 && (
              <div className="flex items-center justify-between mb-4">
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  {tabs.map((tab) => {
                    let tabKey: 'TenantSettings' | 'Theme' | 'DataRefresh' | 'CacheManagement';
                    if (tab === 'Tenant Settings') tabKey = 'TenantSettings';
                    else if (tab === 'Process Data') tabKey = 'DataRefresh';
                    else if (tab === 'Cache Data') tabKey = 'CacheManagement';
                    else tabKey = 'Theme';

                    return (
                      <button
                        key={tab}
                        onClick={() => handleTabChange(tabKey)}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          borderRight: activeTab === tabKey ? '5.62px solid #D12929' : '5.62px solid #474646',
                          borderLeft: activeTab === tabKey ? '5.62px solid #D12929' : '5.62px solid #474646',
                          color: '#fff',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {renderContent()}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Settings;
